import { useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api/client";
import { MenuItem, MenuLabel } from "../components/Menu";
import { Segmented } from "../components/ui";
import { k, t } from "../i18n/i18n";
import { type IaModel, setIaModel, useIaModel } from "./iaModel";

/** Choix du modèle d'IA (Rapide · Avancé), retenu sur ce poste (registres E26, E27 : sur le serveur GPU, « Rapide »
 *  est le modèle Generex, « Avancé » le grand modèle du Cockpit adapté à Generex) : en bas de la barre latérale et
 *  dans l'en-tête de l'assistant, en `Segmented` compact (R8), comme le choix de la langue. Caché tant que le
 *  serveur ne propose qu'un modèle. */

const OPTIONS: { key: IaModel; label: string; title: string }[] = [
  { key: "rapide", label: k("Rapide"), title: k("Modèle rapide : réponses en quelques secondes") },
  { key: "avance", label: k("Avancé"), title: k("Modèle avancé : grand modèle adapté à Generex, rédaction plus riche, environ trois fois plus lente") },
];

/** Le serveur propose-t-il le choix du modèle (deux modèles) ? */
export function useIaOffered(): boolean {
  const { data } = useQuery({ queryKey: ["ai-status"], queryFn: api.aiStatus, staleTime: 15_000 });
  return Boolean(data?.choices?.includes("avance"));
}

/** Serveur GPU en panne : « Avancé » est grisé (les deux choix passent alors par l'IA de secours du serveur). */
function useGpuDown(): boolean {
  const { data } = useQuery({ queryKey: ["ai-status"], queryFn: api.aiStatus, staleTime: 15_000 });
  return Boolean(data?.gpu_down);
}

const DOWN_TITLE = k("Indisponible pendant la panne du serveur GPU : l'IA de secours répond à la place");

/** `fill` : segments sur toute la largeur (dans un menu). */
function useChooseIaModel(): (value: IaModel) => void {
  const queryClient = useQueryClient();
  return (value: IaModel) => {
    setIaModel(value);
    // L'état de l'IA (modèle joignable, source) dépend du choix : relu aussitôt.
    void queryClient.invalidateQueries({ queryKey: ["ai-status"] });
  };
}

export function IaSwitch({ className, fill = false }: { className?: string; fill?: boolean | "natural" }) {
  const current = useIaModel();
  const choose = useChooseIaModel();
  const down = useGpuDown();
  if (!useIaOffered()) return null;
  return (
    <Segmented<IaModel>
      size="sm"
      fill={fill}
      ariaLabel={t("Modèle d'IA")}
      className={className}
      value={current}
      onChange={choose}
      options={OPTIONS.map((option) => {
        const off = down && option.key === "avance";
        return { value: option.key, label: t(option.label), title: t(off ? DOWN_TITLE : option.title), disabled: off };
      })}
    />
  );
}

/** Même choix en entrées de menu à choix exclusif (menu du rail) : parcourues aux flèches comme les autres entrées,
 *  là où un `Segmented` ne serait pas atteint au clavier. */
export function IaMenuItems() {
  const current = useIaModel();
  const choose = useChooseIaModel();
  const down = useGpuDown();
  if (!useIaOffered()) return null;
  return (
    <>
      <MenuLabel>{t("Modèle d'IA")}</MenuLabel>
      {OPTIONS.map((option) => (
        <MenuItem
          key={option.key}
          label={t(option.label)}
          title={t(down && option.key === "avance" ? DOWN_TITLE : option.title)}
          disabled={down && option.key === "avance"}
          selected={current === option.key}
          keepOpen
          onSelect={() => choose(option.key)}
        />
      ))}
    </>
  );
}
